import './SearchSelect.css';
import './../input.scss';
import React, { useState, ReactNode, useRef } from 'react';
import classnames from 'classnames';
import GlobalProps from '../../globalProps';
import { useForm } from '../Form';
import { SelectableList } from '../../SelectableList/SelectableList';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { Tooltip } from '../../Tooltip/Tooltip';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { TextField } from '../TextField/TextField';
import { SimpleCheckbox } from '../Checkbox/Checkbox';
import { writeDB } from '../../../model/store';
import { MostUsedFilters } from '../../../model/state';
import * as PopperJS from 'popper.js';
import { TagsGroup } from '../../Tag/TagsGroup';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface SearchSelectOption {
  id: string;
  label: string;
  content?: ReactNode;
  count?: number;
}

export interface Props extends GlobalProps {
  name?: string;
  label?: string;
  values: SearchSelectOption[];
  hiddenValues?: string[];
  error?: string;
  notFound?: ReactNode;
  description?: string;
  position?: PopperJS.Placement;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  customValue?: string;
  loading?: boolean;
  loadingMsg?: string;
  sortByFrequencyId?: keyof MostUsedFilters;
  disableTagRemove?: boolean;
  onChange?: (value: string) => void;
}

// Methods
function saveToLocalStorage(storageId: keyof MostUsedFilters, idToAdd: string): void {
  // Parse local storage
  const dbFilters = writeDB.get('mostUsedFilters');
  const savedSelection = [...((dbFilters && dbFilters[storageId]) || [])];

  // Look for item
  const itemIndex = savedSelection.findIndex((item) => item.id === idToAdd);
  const item = savedSelection[itemIndex];

  if (item) {
    savedSelection[itemIndex] = {
      ...item,
      count: (item.count += 1),
    };
  } else {
    savedSelection.push({ id: idToAdd, count: 1 });
  }

  writeDB.set('mostUsedFilters', {
    ...dbFilters,
    [storageId]: savedSelection,
  });
}

function sortByFrequency(values: SearchSelectOption[], sortByFrequencyId: keyof MostUsedFilters): SearchSelectOption[] {
  const dbFilters = writeDB.get('mostUsedFilters');
  const savedSelection = dbFilters ? dbFilters[sortByFrequencyId] : [];

  return values
    .map((item) => {
      const localItem = savedSelection?.find((i) => i.id === item.id);
      if (localItem) {
        return {
          ...item,
          count: localItem.count,
        };
      }
      return item;
    })
    .sort((a, b) => {
      if ((a.count || 0) < (b.count || 0)) {
        return 1;
      }
      if ((a.count || 0) > (b.count || 0)) {
        return -1;
      }
      return 0;
    });
}

function getLabelForId(itemId: string, values: SearchSelectOption[]): string {
  const item = values.find((val) => val.id === itemId);
  return item ? item.label : '';
}

// Component
export function SearchSelect({
  name,
  label,
  values,
  hiddenValues,
  error,
  notFound,
  description,
  disabled,
  readonly,
  icon,
  actions,
  required,
  className,
  style,
  customValue,
  onChange,
  loading,
  loadingMsg,
  position = 'bottom-start',
  sortByFrequencyId,
  disableTagRemove,
}: Props): JSX.Element {
  const [rawValue, setValue, triedToSubmit, reportValidity] = useForm(name || '');
  const parentRef = useRef<HTMLDivElement>(null);

  // Parse values
  const value = customValue ?? (rawValue as string);
  const multiselect = Array.isArray(value);
  const selected: string[] = multiselect ? value : value ? [value] : [];
  // Query hook for search
  const [query, setQuery] = useState('');
  // Active hook to display dropdown
  const [active, setActive] = useState(false);
  // Display all options or just the selected ones
  const [displayAllOptions, setDisplayAllOptions] = useState(true);

  const errorCondition = error || (required && triedToSubmit && value.length < 1);

  if (required) {
    reportValidity(!!value);
  }

  // Tag Handling
  function removeTagAtIndex(index: number): void {
    if (multiselect) {
      const newValue = [...value];
      newValue.splice(index, 1);
      setValue(newValue);
    } else {
      setValue('');
      if (onChange) {
        onChange('');
      }
    }
  }

  function addTag(idToAdd: string): void {
    // If multiselect add to list or remove if it exists
    if (multiselect) {
      const newValue = [...value];
      if (newValue.includes(idToAdd)) {
        return removeTagAtIndex(newValue.findIndex((v) => v === idToAdd));
      } else {
        newValue.push(idToAdd);
      }
      setValue(newValue);
    } else {
      // If single select replace value
      setValue(idToAdd);
      setActive(false);
    }
    // On change handle
    if (onChange) {
      onChange(idToAdd);
    }
    // Sort by frequently used
    if (sortByFrequencyId) {
      saveToLocalStorage(sortByFrequencyId, idToAdd);
    }
    // Clear query
    setQuery('');
  }

  // Render
  function renderInput(): JSX.Element {
    return (
      <div className="input__input-wrapper" ref={parentRef}>
        {label && (
          <label className="input__label" htmlFor={name}>
            {label}
            {!required && ` (${t.general.optional})`}
          </label>
        )}
        <TagsGroup
          tags={selected.map((item, index) => ({
            id: index.toString(),
            label: getLabelForId(item, values),
          }))}
          onClear={() => setValue([])}
          onRemove={readonly || disabled ? undefined : removeTagAtIndex}
          disableTagRemove={disableTagRemove}
          actions={
            <div className="search-select__display-options">
              <Icon name="arrow-triangle-down--sm" />
            </div>
          }
        ></TagsGroup>
        {actions && <div className="input__actions">{actions}</div>}
        {errorCondition && <Icon name="alert-circle--sm" className="input__alert-circle" />}
      </div>
    );
  }

  function renderOptions(): React.ReactNode[] {
    let searchResults: string[] | undefined;

    if (query) {
      searchResults = (
        [
          ...values.filter((value) => value.label.toLowerCase().includes(query.trim().toLowerCase())),
        ] as SearchSelectOption[]
      ).map((v) => v.id);
    }

    if (searchResults && searchResults.length === 0) {
      return [
        <div key="search-select__not-found-item" className="search-select__not-found-item">
          {notFound || t.errors.noResults}
        </div>,
      ];
    }

    const sortedValues = sortByFrequencyId ? sortByFrequency(values, sortByFrequencyId) : values;

    return sortedValues
      .filter((v) => !hiddenValues?.find((id) => v.id === id))
      .map((v) => {
        const content = v.content || v.label;
        const isSelected = selected.includes(v.id);

        return (
          <div
            className={classnames('search-select__option', {
              'search-select__option--hidden':
                // If it's not a multiselect and it is selected
                (!multiselect && isSelected) ||
                // Display only selected options
                (!displayAllOptions && !isSelected) ||
                // If searching is active and it's not in the results
                (searchResults && searchResults.length > 0 ? !searchResults?.includes(v.id) : false),
            })}
            key={v.id}
            onClick={() => {
              addTag(v.id);
            }}
          >
            {multiselect ? <SimpleCheckbox value={isSelected} readOnly label={content} /> : content}
          </div>
        );
      });
  }

  if (loading) {
    return <Spinner color="red" text={loadingMsg || t.actions.loading('')} />;
  }

  return (
    <div className={classnames('search-select', 'input', { 'input--with-icon': icon }, className)} style={style}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': multiselect ? value.length > 0 || active : value || active,
          'input__container--error': errorCondition,
        })}
      >
        {disabled || readonly ? (
          renderInput()
        ) : (
          <Tooltip
            style={{ width: parentRef.current?.clientWidth }}
            reference={renderInput()}
            className="search-select__select-menu"
            position={position}
            mode="click"
            gap="none"
            arrow={false}
            rounded={false}
            open={active}
            onChange={(open: boolean) => {
              setActive(open);
            }}
          >
            <>
              {values.length > 4 && (
                <div className="search-select__search">
                  <TextField icon="search--sm" value={query} onChange={(v) => setQuery(v as string)} autoFocus />
                  {multiselect && (
                    <div
                      className={classnames('search-select__toggle-display', {
                        active: !displayAllOptions,
                      })}
                    >
                      <Tooltip
                        reference={
                          <Icon
                            name="list"
                            onClick={() => {
                              setDisplayAllOptions(!displayAllOptions);
                            }}
                          />
                        }
                        darkMode
                        position="top"
                      >
                        <small>{t.actions.displaySelectedOnly}</small>
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}
              {values.length === 0 && <div className="search-select__no-values">{t.errors.noResults}</div>}
              <SelectableList
                className="search-select__select-menu-items"
                options={renderOptions()}
                resetHighlight={false}
              />
            </>
          </Tooltip>
        )}
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={errorCondition ? true : false}>
            <div className="input__error-message">{errorCondition && `${t.errors.required}`}</div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
